/**
 * @fileoverview Mapa de foco das AÇÕES do pareamento — função PURA.
 *
 * Por que existe: a spatial navigation automática do Norigin não é confiável nas
 * telas do app (LRUD strict em Tizen 2017, layout medido por offsetParent, nós de
 * telas irmãs ainda registrados). O projeto já resolve isso com `onArrowPress`
 * explícito (ConfirmDialog, WelcomeScreen, OnboardingModeScreen). A tela de FALHA
 * do pareamento era a exceção: "Tentar de novo" e o botão de saída eram irmãos
 * SEM mapa explícito — ao pousar no de saída, o "Tentar de novo" virava
 * inalcançável (armadilha reportada na LG real).
 *
 * A decisão fica aqui, pura, pra ser provada em teste sem DOM/Norigin: dado o
 * botão atual, a direção e QUAIS botões existem no stage, qual botão recebe o
 * foco. `null` = NÃO HÁ destino nesta direção; o foco FICA onde está.
 *
 * `null` NÃO significa "devolve a seta pro Norigin": o consumidor CONSOME o
 * evento do mesmo jeito (`return false`), igual o ConfirmDialog. Devolver a seta
 * ao Norigin nas pontas seria reabrir exatamente o caminho que este arquivo
 * existe pra fechar — o spatial-nav do Norigin poderia pousar o foco num nó de
 * tela irmã ainda registrado (ou em nada), e a linha de ações é o ÚNICO grupo
 * focável de todo stage do PairingShell (o QR não recebe foco e o link manual é
 * `tabIndex={-1}`), então não há para onde legitimamente sair.
 */

/** Ações possíveis na linha de botões dos stages do pareamento. */
export type PairingAction = 'retry' | 'cancel' | 'extra'

export type ArrowDir = 'up' | 'down' | 'left' | 'right'

export interface PairingActionsLayout {
  /** "Tentar de novo" — só existe em stage error recuperável. */
  retry: boolean
  /** Cancelar/Sair — SEMPRE presente: é a rota de escape da tela. */
  cancel: boolean
  /** Ação extra do caller (ex.: "Sair da conta" no gate de vínculo). */
  extra: boolean
}

/** Ordem visual da linha (flex row, sem wrap). */
const ORDER: readonly PairingAction[] = ['retry', 'cancel', 'extra']

export function presentActions(layout: PairingActionsLayout): PairingAction[] {
  return ORDER.filter((a) => layout[a])
}

/**
 * Vizinho de `from` na direção `dir`, considerando só os botões presentes.
 *
 * - esquerda/direita andam na linha;
 * - cima/baixo são equivalentes a esquerda/direita: o layout é uma linha, mas o
 *   D-pad de TV é usado nos dois eixos e NENHUMA tecla pode virar beco sem saída;
 * - nas pontas devolve `null` = o foco fica onde está e o consumidor consome a
 *   seta (sem armadilha: a direção oposta sempre tem vizinho quando há 2+ botões,
 *   e com 1 botão só não existe outro destino no stage).
 *
 * Garantia provada em teste: com 2+ botões presentes, todo botão alcança todo
 * outro botão (fecho transitivo por esquerda/direita).
 */
export function resolvePairingFocus(
  from: PairingAction,
  dir: ArrowDir,
  layout: PairingActionsLayout,
): PairingAction | null {
  const items = presentActions(layout)
  const i = items.indexOf(from)
  if (i < 0 || items.length < 2) return null

  const back = dir === 'left' || dir === 'up'
  const fwd = dir === 'right' || dir === 'down'
  if (!back && !fwd) return null

  const next = back ? i - 1 : i + 1
  if (next < 0 || next >= items.length) return null
  return items[next] ?? null
}

/** focusKey do botão da ação, no namespace da tela (`account-gate-cancel`, etc). */
export function pairingFocusKey(prefix: string, action: PairingAction): string {
  return `${prefix}-${action}`
}
